import PropTypes from 'prop-types'
import { useState, useRef } from 'react'

// material-ui
import { useTheme, styled } from '@mui/material/styles'
import {
  Avatar,
  Box,
  ButtonBase,
  Card,
  Grid,
  InputAdornment,
  OutlinedInput,
  Popper,
  IconButton
} from '@mui/material'

// third-party
import PopupState, { bindPopper, bindToggle } from 'material-ui-popup-state'

// project imports
import Transitions from '../../../../components/extended/Transitions'

// assets
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import { display, shouldForwardProp } from '@mui/system'
import AdjustIcon from '@mui/icons-material/Adjust'

// styles
const PopperStyle = styled(Popper, { shouldForwardProp })(({ theme }) => ({
  zIndex: 1100,
  width: '99%',
  top: '-55px !important',
  padding: '0 12px',
  [theme.breakpoints.down('sm')]: {
    padding: '0 10px',
  },
}))

const OutlineInputStyle = styled(OutlinedInput, { shouldForwardProp })(
  ({ theme }) => ({
    width: 300,
    marginLeft: 10,
    paddingLeft: 16,
    paddingRight: 16,
    backgroundColor: 'transparent',
    '& input': {
      background: 'transparent !important',
      paddingLeft: '4px !important',
      '::placeholder': {
        color: 'grey',
        opacity: 1,
      },
      // color: '#bdbdbd',
    },
    [theme.breakpoints.down('lg')]: {
      width: 250,
    },
    [theme.breakpoints.down('md')]: {
      width: '100%',
      marginLeft: 4,
      // background: '#fff',
    },
  })
)

const HeaderAvatarStyle = styled(Avatar, { shouldForwardProp })(
  ({ theme }) => ({
    ...theme.typography.commonAvatar,
    ...theme.typography.mediumAvatar,
    background: theme.palette.secondary.light,
    color: theme.palette.secondary.dark,
    '&:hover': {
      background: theme.palette.secondary.dark,
      color: theme.palette.secondary.light,
    },
  })
)

// ==============================|| SEARCH INPUT - MOBILE||============================== //

const MobileSearch = ({ search, setSearch, popupState, handleSearch, label, handleKeyPress }) => {
  const theme = useTheme()

  return (
    <OutlineInputStyle
      id="input-search-header"
      value={search}
      onChange={e => {
        setSearch(e.target.value)
        handleSearch(e)
      }}
      onKeyDown={handleKeyPress}
      placeholder={label}
      startAdornment={
        <InputAdornment position="start">
          <SearchIcon
            stroke={1.5}
            size="1rem"
            sx={{ color: theme.palette.grey[500] }}
          />
        </InputAdornment>
      }
      endAdornment={
        <InputAdornment position="end">
          <Box sx={{ ml: 2 }}>
            <ButtonBase>
              <Avatar
                variant="rounded"
                sx={{
                  backgroundColor: 'transparent',
                  color: theme.palette.grey[600],
                  boxShadow: 'none',
                  width: 32,
                  height: 32,
                  cursor: 'pointer'
                }}
                {...bindToggle(popupState)}
              >
                <CloseIcon
                  onClick={() => setSearch('')}
                  stroke={1.5} size="1.3rem"
                />
              </Avatar>
            </ButtonBase>
          </Box>
        </InputAdornment>
      }
      aria-describedby="search-helper-text"
      inputProps={{ 'aria-label': 'weight' }}
    />
  )
}

MobileSearch.propTypes = {
  search: PropTypes.string,
  setSearch: PropTypes.func,
  popupState: PropTypes.any,
}

// ==============================|| SEARCH INPUT ||============================== //

const SearchSection = ({ handleSearch, search, setSearch, label }) => {
  const theme = useTheme()
  const iconRef = useRef(null);
  const [searchPosition, setSearchPosition] = useState({ top: 0, left: 0 });
  const [iconRect, setIconRect] = useState(null);
  const [isLeft, setIsLeft] = useState(false);
  const [fieldWidth, setFieldWidth] = useState(0);

  const handleToggle = (popupState) => (event) => {
    if (!iconRef.current) return;

    const rect = iconRef.current.getBoundingClientRect();
    const screenWidth = window.innerWidth;
    const fieldWidth = 250;

    const isOnRight = rect.left > screenWidth / 2;

    setIsLeft(isOnRight);
    setIconRect(rect);

    const top = 10;
    const left = isOnRight
      ? rect.left - fieldWidth - 106
      : rect.right + 8;

    // Save calculated position
    setSearchPosition({ top, left });

    // Toggle the popup state
    popupState.toggle(event);
  };


  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch(e);
    }
  };

  return (
    <>
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <PopupState variant="popper" popupId="demo-popup-popper">
          {popupState => (
            <>
              <Box sx={{ ml: 2 }}>
                <ButtonBase onClick={handleToggle(popupState)} ref={iconRef}>
                  <HeaderAvatarStyle
                    variant="rounded"
                    {...bindToggle(popupState)}
                  >
                    <SearchIcon stroke={1.5} size="1.2rem" />
                  </HeaderAvatarStyle>
                </ButtonBase>
              </Box>
              <PopperStyle
                {...bindPopper(popupState)}
                transition
                anchorEl={popupState.anchorEl}
                // placement={placement}
                modifiers={[
                  {
                    name: 'offset',
                    options: {
                      // offset: offset, 
                    },
                  },
                  {
                    name: 'preventOverflow',
                    options: {
                      boundary: 'viewport',
                    },
                  },
                ]}
              >
                {({ TransitionProps }) => (
                  <>
                    <Transitions
                      type="zoom"
                      {...TransitionProps}
                      sx={{
                        transformOrigin: 'center left',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: `${searchPosition.top}px`,
                          left: `${searchPosition.left}px`,
                          zIndex: 1300,
                        }}
                      >
                        <Card
                          sx={{
                            display: 'block',

                            [theme.breakpoints.down('sm')]: {
                              border: 0,
                              boxShadow: 'none',
                            },
                          }}
                        >
                          <Box sx={{ p: 1 }}>
                            <Grid
                              container
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Grid item xs>
                                <MobileSearch
                                  search={search}
                                  setSearch={setSearch}
                                  popupState={popupState}
                                  handleSearch={handleSearch}
                                  handleKeyPress={handleKeyPress}
                                  label={label}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        </Card>
                      </Box>
                    </Transitions>
                  </>
                )}
              </PopperStyle>
            </>
          )
          }
        </PopupState>
      </Box>
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <OutlineInputStyle
          id="input-search-header"
          value={search}
          onChange={e => {
            setSearch(e.target.value)
            handleSearch(e)
          }}
          onKeyDown={handleKeyPress}
          placeholder={label}
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon
                stroke={1.5}
                size="1rem"
                sx={{ color: theme.palette.grey[500] }}

              />
            </InputAdornment>
          }
          endAdornment={
            search && (
              <InputAdornment position="end">
                <IconButton onClick={() => setSearch('')}>
                  <CloseIcon
                    sx={{ color: "white" }} />
                </IconButton>
              </InputAdornment>
            )
          }
          aria-describedby="search-helper-text"
          inputProps={{ 'aria-label': 'weight' }}
        />
      </Box>
    </>
  )
}

export default SearchSection
